// componenets/socials/profile/SearchPeople.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FiChevronLeft, FiSearch, FiX } from "react-icons/fi";
import { db } from "../../../firebase/firebase.utils";
import Avatar from "../../retro/avatar/avatar.component";
import { fetchUsersByIds, firstProfilePhoto } from "./profileApi";
import "./profile.styles.scss";
import "./SearchPeople.additions.scss";

const uniqueUsers = (users) => [
  ...new Map(
    users.filter((user) => user?.uid).map((user) => [user.uid, user]),
  ).values(),
];

// With no props this is still the existing profile-search route.
export default function SearchPeople({
  title = "Search people",
  description = "Find people by username or exact email.",
  includeSelf = false,
  embedded = false,
  backButtonAction,
  disableRowPress = false,
  renderPreInformation,
  renderRowAction,
}) {
  const currentUser = useSelector((state) => state.user.currentUser);
  const navigate = useNavigate();
  const requestNumber = useRef(0);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [error, setError] = useState("");
  const [relationships, setRelationships] = useState({
    connected: new Set(),
    outgoing: new Set(),
    incoming: new Set(),
  });
  const blocked = useMemo(
    () => new Set(currentUser?.blockedUsers || []),
    [currentUser?.blockedUsers],
  );

  useEffect(() => {
    let active = true;
    setRecent([]);
    setRelationships({
      connected: new Set(),
      outgoing: new Set(),
      incoming: new Set(),
    });
    if (!currentUser?.uid) return;
    const load = async () => {
      setRecentLoading(true);
      try {
        const [connections, outgoing, incoming] = await Promise.all([
          db
            .collection("connections")
            .where("uids", "array-contains", currentUser.uid)
            .limit(300)
            .get(),
          db
            .collection("connectionRequests")
            .where("fromUid", "==", currentUser.uid)
            .where("status", "==", "pending")
            .limit(300)
            .get(),
          db
            .collection("connectionRequests")
            .where("toUid", "==", currentUser.uid)
            .where("status", "==", "pending")
            .limit(300)
            .get(),
        ]);
        const otherUid = (document) =>
          (document.data().uids || []).find((uid) => uid !== currentUser.uid);
        const connected = new Set(
          connections.docs.map(otherUid).filter(Boolean),
        );
        const recentIds = [...connections.docs]
          .sort((a, b) => {
            const time = (document) =>
              document.data().updatedAt?.toMillis?.() ||
              document.data().createdAt?.toMillis?.() ||
              0;
            return time(b) - time(a);
          })
          .map(otherUid)
          .filter((uid) => uid && !blocked.has(uid))
          .slice(0, 10);
        const recentUsers = await fetchUsersByIds(recentIds);
        const recentById = new Map(recentUsers.map((user) => [user.uid, user]));
        if (!active) return;
        setRelationships({
          connected,
          outgoing: new Set(
            outgoing.docs
              .map((document) => document.data().toUid)
              .filter(Boolean),
          ),
          incoming: new Set(
            incoming.docs
              .map((document) => document.data().fromUid)
              .filter(Boolean),
          ),
        });
        setRecent(recentIds.map((uid) => recentById.get(uid)).filter(Boolean));
      } catch (err) {
        console.log("People relationship load error:", err);
      } finally {
        if (active) setRecentLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [currentUser?.uid, blocked]);

  const runSearch = useCallback(
    async (rawText, requestId) => {
      const typed = rawText.trim();
      try {
        const users = db.collection("users");
        const snapshots = typed.includes("@")
          ? await Promise.all(
              [...new Set([typed, typed.toLowerCase()])].map((email) =>
                users.where("email", "==", email).limit(20).get(),
              ),
            )
          : await Promise.all([
              users
                .where("userName", ">=", typed)
                .where("userName", "<=", `${typed}\uf8ff`)
                .limit(20)
                .get(),
              users
                .where("userName", "==", typed.toLowerCase())
                .limit(10)
                .get(),
            ]);
        if (requestId !== requestNumber.current) return;
        const found = snapshots.flatMap((snapshot) =>
          snapshot.docs.map((document) => ({
            ...document.data(),
            uid: document.id,
            id: document.id,
          })),
        );
        setResults(
          uniqueUsers(found).filter(
            (user) =>
              (includeSelf || user.uid !== currentUser?.uid) &&
              !blocked.has(user.uid),
          ),
        );
      } catch (err) {
        if (requestId !== requestNumber.current) return;
        console.log("People search error:", err);
        setError("Couldn't search people. Please try again.");
      } finally {
        if (requestId === requestNumber.current) setLoading(false);
      }
    },
    [currentUser?.uid, blocked, includeSelf],
  );

  useEffect(() => {
    const requestId = ++requestNumber.current;
    const shouldSearch = Boolean(currentUser?.uid && text.trim().length >= 2);
    setResults([]);
    setError("");
    setSearched(shouldSearch);
    setLoading(shouldSearch);
    const timer = shouldSearch
      ? window.setTimeout(() => runSearch(text, requestId), 300)
      : null;
    return () => {
      window.clearTimeout(timer);
      requestNumber.current += 1; // Ignore responses for old queries or an unmounted picker.
    };
  }, [text, runSearch, currentUser?.uid]);

  const badgeFor = (uid) => {
    if (relationships.connected.has(uid)) return "Connected";
    if (relationships.outgoing.has(uid)) return "Requested";
    if (relationships.incoming.has(uid)) return "Incoming";
    return null;
  };
  const showingRecent = text.trim().length < 2 && !searched;
  const shownUsers = (showingRecent ? recent : results).filter(
    (user) => !blocked.has(user.uid),
  );
  const waiting = showingRecent ? recentLoading : loading;
  const openProfile = (user) =>
    navigate(`/profile/${user.uid}`, { state: { user } });
  const personContent = (user) => (
    <>
      <Avatar user={user} src={firstProfilePhoto(user.imageUrl)} size={44} />
      <span>
        <strong>{user.userName || "Unknown"}</strong>
        {user.headline && <small>{user.headline}</small>}
      </span>
    </>
  );

  return (
    <main
      className={`social-route-page people-page${embedded ? " people-page--embedded" : ""}`}
    >
      <header className="social-page-header">
        <button
          type="button"
          className="icon-button"
          aria-label={embedded ? "Close search" : "Go back"}
          onClick={backButtonAction || (() => navigate(-1))}
        >
          {embedded ? <FiX /> : <FiChevronLeft />}
        </button>
        <div>
          <h1>{title}</h1>
          {description && <p>{description}</p>}
        </div>
      </header>
      {renderPreInformation && (
        <div className="people-page__pre-information">
          {renderPreInformation()}
        </div>
      )}
      <div className="people-page__content">
        <label className="people-search">
          <FiSearch aria-hidden="true" />
          <input
            autoFocus
            aria-label="Search by username or email"
            value={text}
            placeholder="Search by username or email"
            onChange={(event) => {
              requestNumber.current += 1;
              setText(event.target.value);
            }}
          />
          {loading ? (
            <span aria-label="Searching">…</span>
          ) : (
            text && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  requestNumber.current += 1;
                  setText("");
                }}
              >
                <FiX />
              </button>
            )
          )}
        </label>
        <small>Type at least 2 characters. Emails must match exactly.</small>
        <h2>{showingRecent ? "Recent connections" : "Results"}</h2>
        {error && <p role="alert">{error}</p>}
        <div className="people-list" aria-busy={waiting}>
          {shownUsers.map((user) => {
            const badge = badgeFor(user.uid);
            if (!renderRowAction && !disableRowPress)
              return (
                <button
                  type="button"
                  key={user.uid}
                  onClick={() => openProfile(user)}
                >
                  {personContent(user)}
                  {badge && <em>{badge}</em>}
                </button>
              );
            return (
              <div className="people-list__row" key={user.uid}>
                {disableRowPress ? (
                  <div className="people-list__person">
                    {personContent(user)}
                  </div>
                ) : (
                  <button
                    type="button"
                    className="people-list__person"
                    onClick={() => openProfile(user)}
                  >
                    {personContent(user)}
                  </button>
                )}
                <div className="people-list__actions">
                  {badge && <em>{badge}</em>}
                  {renderRowAction?.({ user, badge })}
                </div>
              </div>
            );
          })}
          {waiting && !shownUsers.length && (
            <div className="people-list__empty" role="status">
              {showingRecent ? "Loading recent connections…" : "Searching…"}
            </div>
          )}
          {!waiting && !error && !shownUsers.length && (
            <div className="people-list__empty">
              {searched
                ? "No matching people found."
                : "No recent connections yet."}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
